import { useCollectionNavigation } from '../../ui/collection-navigation.js';
import { GiftCardBadge } from './GiftCardBadge.js';
import { ClientAvatar } from './ClientAvatar.js';
import { formatCrmMoney } from './crm-money.js';
import {
  SESSION_STATUS_LABELS,
  type SessionStatus,
  type SessionSummary,
  type DeleteCrmShootRequest,
} from '@diana/contracts';
import {
  AtSign,
  Mail,
  Phone,
  Plus,
  Columns3,
  List,
  MoreHorizontal,
  CircleCheck,
  CircleX,
  Gift,
  ChevronDown,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Badge, EmptyState, CollectionSearch, Select } from '../../ui/index.js';
import { formatShortDate, primaryContact, sessionStatuses, statusTone } from './crm-format.js';
import {
  calendarDaysSinceScheduled,
  filterShoots,
  groupShoots,
  shootClientSummary,
  type ShootStatusFilter,
} from './shoot-list.js';
import {
  ShootBoardDrag,
  BoardDropColumn,
  BoardDragCard,
  type MoveShoot,
} from './ShootBoardDrag.js';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ShootActionMenuItems } from './ShootActionMenuItems.js';
import { ShootDestructiveDialog } from './ShootDestructiveDialog.js';
import { useDianaUIPortal } from '../../ui/index.js';
import './CrmShootsList.css';

export function CrmShootsList({
  shoots,
  requestNotes,
  onMove,
  deletionPreview,
  remove,
}: {
  shoots: readonly SessionSummary[];
  deletionPreview?: (shoot: SessionSummary) => Promise<{
    revision: number;
    client: { id: string; name: string; revision: number; canDelete: boolean };
  }>;
  remove?: (shoot: SessionSummary, input: DeleteCrmShootRequest) => Promise<void>;
  onMove?: MoveShoot | undefined;
  requestNotes?: Readonly<Record<string, string | null | undefined>> | undefined;
}) {
  const navigate = useNavigate();
  const [dashboardParams, setDashboardParams] = useSearchParams();
  const movingRef = useRef(false);
  const destructiveTrigger = useRef<HTMLButtonElement | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [moveFeedback, setMoveFeedback] = useState<{ error: boolean; text: string } | null>(null);
  const [destructive, setDestructive] = useState<{
    shoot: SessionSummary;
    mode: 'cancel' | 'delete';
  } | null>(null);
  const moveTo = async (shoot: SessionSummary, next: SessionStatus) => {
    if (movingRef.current || !onMove) return false;
    if (next === 'Scheduled') {
      navigate(`/crm/shoots/${shoot.id}`, { state: { quickAction: 'schedule' } });
      return false;
    }
    movingRef.current = true;
    setMoving(shoot.id);
    setMoveFeedback(null);
    try {
      const moved = await onMove(shoot, next);
      if (moved !== false)
        setMoveFeedback({ error: false, text: `Moved to ${SESSION_STATUS_LABELS[next]}.` });
      return moved !== false;
    } catch {
      setMoveFeedback({
        error: true,
        text: 'Could not change the status. Refresh the list and check the shoot before trying again.',
      });
      return false;
    } finally {
      movingRef.current = false;
      setMoving(null);
    }
  };
  const {
    value: listContext,
    setValue: setListContext,
    rootRef,
    remember,
  } = useCollectionNavigation('crm-shoots', () => ({
    search: '',
    status: 'All' as ShootStatusFilter,
    view: readShootView(),
    giftsExpanded: false,
  }));
  const { search, status, view } = listContext;
  useEffect(() => {
    const requested = dashboardParams.get('status');
    if (!requested || !sessionStatuses.includes(requested as SessionStatus)) return;
    setListContext((current) => ({
      ...current,
      search: '',
      status: requested as SessionStatus,
      view: 'list',
      giftsExpanded: requested === 'InDiscussion',
    }));
    const next = new URLSearchParams(dashboardParams);
    next.delete('status');
    next.delete('week');
    setDashboardParams(next, { replace: true });
  }, [dashboardParams, setDashboardParams, setListContext]);
  const setSearch = (search: string) =>
    setListContext((current) => ({
      ...current,
      search,
      giftsExpanded: search.trim() ? true : current.giftsExpanded,
    }));
  const setStatus = (status: ShootStatusFilter) =>
    setListContext((current) => ({
      ...current,
      status,
      giftsExpanded: status === 'InDiscussion' ? true : current.giftsExpanded,
    }));
  const setView = (view: 'list' | 'board') => setListContext((current) => ({ ...current, view }));
  const desktop = useSyncExternalStore(subscribeDesktop, isDesktop, () => false);
  const showBoard = desktop && view === 'board';
  const chooseView = (next: 'list' | 'board') => {
    setView(next);
    try {
      localStorage.setItem('diana.crm.shoots.view', next);
    } catch {
      /* Storage can be disabled. */
    }
  };
  const newCount = shoots.filter((shoot) => shoot.status === 'NewRequest').length;
  const effectiveStatus = showBoard && status === 'Canceled' ? 'All' : status;
  const matches = useMemo(
    () => filterShoots(shoots, { search, status: effectiveStatus }),
    [shoots, search, effectiveStatus],
  );
  const groups = useMemo(() => groupShoots(matches), [matches]);
  const now = Date.now();
  const waitingGifts = groups.discussion.filter((shoot) => shoot.isGiftCard);
  const regularRows = [
    ...groups.discussion.filter((shoot) => !shoot.isGiftCard),
    ...groups.upcoming,
    ...groups.deliver,
    ...groups.past,
  ];
  const renderRows = (items: readonly SessionSummary[], label: string) =>
    items.length ? (
      <section className="shoots-list__rows" aria-label={label}>
        <div className="shoots-list__columns" aria-hidden="true">
          <span>Status</span>
          <span>Client</span>
          <span>Package</span>
          <span>Date & time</span>
        </div>
        {items.map((shoot) => {
          const days =
            shoot.status === 'Editing'
              ? calendarDaysSinceScheduled(shoot.scheduledStartAt, now)
              : undefined;
          const client = shootClientSummary(shoot).primaryClient;
          return (
            <QuickMoveCard
              key={shoot.id}
              shoot={shoot}
              enabled={desktop && Boolean(onMove)}
              moving={moving}
              canDelete={Boolean(remove && deletionPreview)}
              onEdit={(action) =>
                navigate(`/crm/shoots/${shoot.id}`, { state: { quickAction: action } })
              }
              onDestructive={(mode, trigger) => {
                destructiveTrigger.current = trigger;
                setDestructive({ shoot, mode });
              }}
              onMove={(status) => void moveTo(shoot, status)}
            >
              <Link
                className={`shoot-timeline-card shoot-timeline-card--${shoot.status}`}
                to={`/crm/shoots/${shoot.id}`}
              >
                <span className="shoot-timeline-card__heading">
                  <ShootStatus shoot={shoot} />
                  <span className="shoot-timeline-card__client">
                    <ClientAvatar
                      id={client.id}
                      name={client.name}
                      className="shoot-timeline-card__avatar"
                    />
                    <ShootIdentity shoot={shoot} showContact={false} />
                  </span>
                </span>
                <ShootPackage shoot={shoot} />
                <span className="shoot-timeline-card__date">
                  {shoot.scheduledStartAt ? (
                    <span className="shoot-timeline-card__datetime">
                      <time dateTime={shoot.scheduledStartAt}>
                        {formatTimelineDate(
                          shoot.scheduledStartAt,
                          now,
                          shoot.status === 'Scheduled',
                        )}
                      </time>
                      <span>{formatTime(shoot.scheduledStartAt)}</span>
                    </span>
                  ) : null}
                  {days !== undefined ? (
                    <span className="shoot-timeline-card__elapsed">
                      {days === 0
                        ? 'Scheduled today'
                        : `${days} ${days === 1 ? 'day' : 'days'} since shoot`}
                    </span>
                  ) : null}
                </span>
              </Link>
            </QuickMoveCard>
          );
        })}
      </section>
    ) : null;

  return (
    <article
      ref={rootRef}
      onClickCapture={remember}
      onKeyDownCapture={remember}
      onScrollCapture={remember}
      className={showBoard ? 'shoots-list shoots-list--board' : 'shoots-list'}
    >
      <header className="shoots-list__heading">
        <div className="shoots-list__title">
          <h1>Shoots</h1>
          <span>{shoots.length} total</span>
          {newCount ? <Badge tone="warning">{newCount} new</Badge> : null}
        </div>
        <div className="shoots-list__toolbar">
          <Link
            className="shoots-list__create"
            to="/crm/shoots/new"
            aria-label="New shoot"
            title="New shoot"
          >
            <Plus className="shoots-list__create-icon" size={20} aria-hidden="true" />
            <span>New shoot</span>
          </Link>
          <CollectionSearch
            className="shoots-list__search"
            label="Search shoots"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Select
            wrapperClassName="shoots-list__status-filter-wrap"
            className="shoots-list__status-filter"
            aria-label="Filter shoots by status"
            value={effectiveStatus}
            onChange={(event) => setStatus(event.currentTarget.value as ShootStatusFilter)}
          >
            <option value="All">All shoots</option>
            {sessionStatuses
              .filter((item) => !showBoard || item !== 'Canceled')
              .map((item) => (
                <option key={item} value={item}>
                  {SESSION_STATUS_LABELS[item]}
                </option>
              ))}
          </Select>
          {desktop && (
            <div className="shoots-list__views" role="group" aria-label="Shoot view">
              <button
                type="button"
                aria-label="List"
                title="List"
                aria-pressed={view === 'list'}
                onClick={() => chooseView('list')}
              >
                <List size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Board"
                title="Board"
                aria-pressed={view === 'board'}
                onClick={() => chooseView('board')}
              >
                <Columns3 size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </header>

      {destructive ? (
        <ShootDestructiveDialog
          mode={destructive.mode}
          name={shootClientSummary(destructive.shoot).primaryClient.name}
          revision={destructive.shoot.revision}
          preview={deletionPreview ? () => deletionPreview(destructive.shoot) : undefined}
          remove={remove ? (input) => remove(destructive.shoot, input) : undefined}
          cancel={async () => {
            if (!(await moveTo(destructive.shoot, 'Canceled')))
              throw new Error('Cancellation not saved');
          }}
          close={() => {
            setDestructive(null);
            queueMicrotask(() => destructiveTrigger.current?.focus());
          }}
        />
      ) : null}
      {moveFeedback ? (
        <p role={moveFeedback.error ? 'alert' : 'status'}>{moveFeedback.text}</p>
      ) : null}
      {showBoard ? (
        <ShootBoardDrag onMove={onMove}>
          <ShootBoard groups={groups} status={effectiveStatus} notes={requestNotes} now={now} />
        </ShootBoardDrag>
      ) : (
        <>
          {groups.requests.length > 0 && (
            <section className="shoots-list__request-group" aria-labelledby="shoots-requests">
              <div className="shoots-list__section-heading">
                <h2 id="shoots-requests">New requests</h2>
                <span>{groups.requests.length}</span>
              </div>
              <div className="shoots-list__requests">
                {groups.requests.map((shoot) => (
                  <QuickMoveCard
                    key={shoot.id}
                    request
                    shoot={shoot}

                    enabled={desktop && Boolean(onMove)}
                    moving={moving}
                    canDelete={Boolean(remove && deletionPreview)}
                    onEdit={(action) =>
                      navigate(`/crm/shoots/${shoot.id}`, { state: { quickAction: action } })
                    }
                    onDestructive={(mode, trigger) => {
                      destructiveTrigger.current = trigger;
                      setDestructive({ shoot, mode });
                    }}
                    onMove={(status) => void moveTo(shoot, status)}
                  >
                    <Link className="shoots-list__request" to={`/crm/shoots/${shoot.id}`}>
                      <div className="shoots-list__request-heading">
                        <ShootIdentity shoot={shoot} compact />
                        <time
                          className="shoots-list__request-age"
                          dateTime={shoot.createdAt}
                          title={`Received ${formatShortDate(shoot.createdAt)}`}
                        >
                          {requestAge(shoot.createdAt, now)}
                        </time>
                      </div>
                      <div className="shoots-list__request-summary">
                        <span>{shoot.requestedPackageTitle ?? 'Photography'}</span>
                        {shoot.isGiftCard ? <GiftCardBadge /> : null}
                        {shoot.priceAmountMinor !== null && (
                          <span>{formatCrmMoney(shoot.priceAmountMinor, shoot.currency)}</span>
                        )}
                      </div>
                      {requestNotes?.[shoot.id] ? (
                        <div className="shoots-list__message">
                          <p>{requestNotes[shoot.id]}</p>
                        </div>
                      ) : null}
                    </Link>
                  </QuickMoveCard>
                ))}
              </div>
            </section>
          )}

          {waitingGifts.length ? (
            <details
              className="shoots-list__gift-group"
              aria-label="Gift cards waiting"
              open={Boolean(listContext.giftsExpanded)}
            >
              <summary
                onClick={(event) => {
                  event.preventDefault();
                  setListContext((current) => ({
                    ...current,
                    giftsExpanded: !current.giftsExpanded,
                  }));
                }}
              >
                <Gift size={18} aria-hidden="true" />
                <span>Gift cards</span>
                <span className="shoots-list__gift-count">{waitingGifts.length}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </summary>
              {renderRows(waitingGifts, 'Gift card shoots')}
            </details>
          ) : null}
          {renderRows(regularRows, 'Shoots list')}
        </>
      )}
      {shoots.length === 0 ? (
        <EmptyState
          title="No shoots yet"
          description="New enquiries from the website will appear here as shoots."
        />
      ) : matches.length === 0 ? (
        <EmptyState
          title="No matching shoots"
          description="Try another search or status filter."
          action={
            <button
              className="shoots-list__clear"
              type="button"
              onClick={() => {
                setSearch('');
                setStatus('All');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : null}
    </article>
  );
}

function ShootIdentity({
  shoot,
  compact = false,
  showContact = true,
}: {
  shoot: SessionSummary;
  compact?: boolean;
  showContact?: boolean;
}) {
  const { primaryClient: client, additionalClientCount } = shootClientSummary(shoot);
  return (
    <span className="shoots-list__identity">
      <strong>{client.name}</strong>
      {additionalClientCount > 0 && (
        <span className="shoots-list__additional">
          +{additionalClientCount} more
          <span className="sr-only">
            :{' '}
            {shoot.clients
              .filter((item) => item !== client)
              .map((item) => item.name)
              .join(', ')}
          </span>
        </span>
      )}
      {showContact && (
        <span
          className={
            compact ? 'shoots-list__contact shoots-list__contact--compact' : 'shoots-list__contact'
          }
        >
          {compact &&
            (client.email ? (
              <Mail size={13} aria-hidden="true" />
            ) : client.phone ? (
              <Phone size={13} aria-hidden="true" />
            ) : client.instagram ? (
              <AtSign size={13} aria-hidden="true" />
            ) : null)}
          <span>{primaryContact(client)}</span>
        </span>
      )}
    </span>
  );
}
function QuickMoveCard({
  children,
  shoot,
  enabled,
  moving,
  onMove,
  onEdit,
  onDestructive,
  canDelete,
  request = false,
}: {
  children: ReactNode;
  shoot: SessionSummary;
  enabled: boolean;
  moving: string | null;
  canDelete: boolean;
  onMove(status: SessionStatus): void;
  onEdit(action: 'edit' | 'schedule' | 'reschedule'): void;
  onDestructive(mode: 'cancel' | 'delete', trigger: HTMLButtonElement | null): void;
  request?: boolean;
}) {
  const portal = useDianaUIPortal();
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <div
      className={`shoot-list-item${request ? ' shoot-list-item--request' : ''}`}
      data-has-next={enabled || undefined}
    >
      {children}
      {enabled ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className="shoot-list-item__next"
            ref={trigger}
            aria-label={`Actions for ${shootClientSummary(shoot).primaryClient.name}`}
            disabled={moving !== null}
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal container={portal}>
            <DropdownMenu.Content className="dui-menu shoot-menu" sideOffset={6} align="end">
              <ShootActionMenuItems
                status={shoot.status}
                busy={moving !== null}
                canDelete={canDelete}
                onEdit={() => onEdit('edit')}
                onSchedule={onEdit}
                onCancel={() => onDestructive('cancel', trigger.current)}
                onDelete={() => onDestructive('delete', trigger.current)}
                onMove={onMove}
              />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}
    </div>
  );
}

function ShootPackage({ shoot }: { shoot: SessionSummary }) {
  return (
    <span className="shoots-list__package">
      <strong>{shoot.requestedPackageTitle ?? 'Photography'}</strong>
      {shoot.isGiftCard ? <GiftCardBadge /> : null}
      {shoot.priceAmountMinor !== null && (
        <span>{formatCrmMoney(shoot.priceAmountMinor, shoot.currency)}</span>
      )}
    </span>
  );
}
function ShootStatus({ shoot }: { shoot: SessionSummary }) {
  const terminal = shoot.status === 'Delivered' || shoot.status === 'Canceled';
  const Icon = shoot.status === 'Delivered' ? CircleCheck : CircleX;
  const label = SESSION_STATUS_LABELS[shoot.status];
  return (
    <span className={`shoots-list__status shoots-list__status--${shoot.status}`}>
      {terminal ? (
        <span className="shoots-list__terminal-status" role="img" aria-label={label} title={label}>
          <Icon size={19} strokeWidth={1.5} aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </span>
      ) : (
        <Badge tone={statusTone(shoot.status)}>{label}</Badge>
      )}
    </span>
  );
}
function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(value),
  );
}

function requestAge(value: string, now: number) {
  const minutes = Math.max(0, Math.floor((now - Date.parse(value)) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / 1440)}d ago`;
}

function formatTimelineDate(value: string, now: number, withWeekday: boolean) {
  const date = new Date(value);
  return new Intl.DateTimeFormat('en-GB', {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    day: '2-digit',
    month: 'short',
    ...(date.getFullYear() !== new Date(now).getFullYear() ? { year: 'numeric' as const } : {}),
  }).format(date);
}

const shootViewKey = 'diana.crm.shoots.view';
function readShootView(): 'list' | 'board' {
  try {
    return localStorage.getItem(shootViewKey) === 'board' ? 'board' : 'list';
  } catch {
    return 'list';
  }
}
function isDesktop() {
  return (
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches
  );
}
function subscribeDesktop(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia('(min-width: 1024px)');
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
function ShootBoard({
  groups,
  status,
  notes,
  now,
}: {
  groups: ReturnType<typeof groupShoots>;
  status: ShootStatusFilter;
  notes: Readonly<Record<string, string | null | undefined>> | undefined;
  now: number;
}) {
  const columns = (status === 'All' ? sessionStatuses : [status]).filter(
    (item) => item !== 'Canceled',
  );
  const sorted = [
    ...groups.requests,
    ...groups.discussion,
    ...groups.upcoming,
    ...groups.deliver,
    ...groups.past,
  ];
  return (
    <div className="shoot-board" aria-label="Shoots by status" data-collection-scroll>
      {columns.map((column) => {
        const items = sorted.filter((shoot) => shoot.status === column);
        return (
          <BoardDropColumn key={column} status={column}>
            <header className="shoot-board__heading">
              <h2>{SESSION_STATUS_LABELS[column]}</h2>
              <span>{items.length}</span>
            </header>
            <div className="shoot-board__cards">
              {items.map((shoot) => {
                const days =
                  column === 'Editing'
                    ? calendarDaysSinceScheduled(shoot.scheduledStartAt, now)
                    : undefined;
                return (
                  <BoardDragCard shoot={shoot} key={shoot.id}>
                    {column === 'Scheduled' && <BoardDate shoot={shoot} now={now} />}
                    <div className="shoot-board__identity">
                      <ShootIdentity shoot={shoot} showContact={false} />
                      {column === 'NewRequest' && (
                        <time
                          dateTime={shoot.createdAt}
                          title={`Received ${formatShortDate(shoot.createdAt)}`}
                        >
                          {requestAge(shoot.createdAt, now)}
                        </time>
                      )}
                    </div>
                    {column !== 'NewRequest' &&
                      column !== 'Scheduled' &&
                      (shoot.scheduledStartAt || column !== 'InDiscussion') && (
                        <BoardDate shoot={shoot} now={now} />
                      )}
                    <ShootPackage shoot={shoot} />
                    {column === 'NewRequest' && notes?.[shoot.id] && (
                      <div className="shoots-list__message">
                        <p>{notes[shoot.id]}</p>
                      </div>
                    )}
                    {days !== undefined && (
                      <span className="shoot-board__elapsed">
                        {days === 0
                          ? 'Scheduled today'
                          : `${days} ${days === 1 ? 'day' : 'days'} since shoot`}
                      </span>
                    )}
                  </BoardDragCard>
                );
              })}
              {items.length === 0 && <p className="shoot-board__empty">No shoots</p>}
            </div>
          </BoardDropColumn>
        );
      })}
    </div>
  );
}
function BoardDate({ shoot, now }: { shoot: SessionSummary; now: number }) {
  return (
    <div className="shoot-board__date">
      {shoot.scheduledStartAt ? (
        <>
          <time dateTime={shoot.scheduledStartAt}>
            {formatTimelineDate(shoot.scheduledStartAt, now, shoot.status === 'Scheduled')}
          </time>
          <span>{formatTime(shoot.scheduledStartAt)}</span>
        </>
      ) : (
        <span>Date not set</span>
      )}
    </div>
  );
}
